import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon, CreditCardIcon, ExternalLinkIcon, SparklesIcon } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/components/ui/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubscriptionUpgrade } from "@/hooks/auth/use-subscription-upgrade";
import { useSubscriptions } from "@/hooks/query/billing";
import { subscription, transformAuthResponse } from "@/lib/auth-client";
import { noIndexSeo } from "@/lib/seo";
import { CREDIT_PACKS } from "@/lib/subscriptions/stripe-plans";
import { getPlanByName, getPlans } from "@/lib/subscriptions/subscriptions";
import * as m from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { $createCreditPackCheckout } from "@/server/actions/credit-pack";

export const Route = createFileRoute("/(app)/settings/subscription")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.subscription_title()} | StudyOS`,
			description: m.subscription_description(),
			pathname: match.pathname,
		}),
});

function getStatusLabels(): Record<string, string> {
	return {
		active: m.status_active(),
		trialing: m.status_trialing(),
		canceled: m.status_canceled(),
		incomplete: m.status_incomplete(),
		past_due: m.status_pastDue(),
		unpaid: m.status_unpaid(),
		paused: m.status_paused(),
	};
}

const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
	active: "default",
	trialing: "secondary",
	canceled: "destructive",
	incomplete: "outline",
	past_due: "destructive",
	unpaid: "destructive",
	paused: "outline",
};

function RouteComponent() {
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium">{m.subscription_title()}</h3>
				<p className="text-sm text-muted-foreground">{m.subscription_description()}</p>
			</div>
			<Separator />
			<CurrentSubscription />
			<PlanSelector />
			<CreditPackSelector />
		</div>
	);
}

function CurrentSubscription() {
	const { data: subscriptions, isPending } = useSubscriptions();

	const { mutate: cancel, isPending: isCanceling } = useMutation({
		mutationKey: ["subscription", "cancel"],
		mutationFn: async (subscriptionId: string) => {
			const result = await subscription.cancel(
				{
					subscriptionId,
					returnUrl: "/settings/subscription",
				},
				{ throw: true },
			);

			return result;
		},
		meta: {
			success: { message: m.subscription_cancelRedirect() },
			error: { message: m.subscription_cancelFailed() },
		},
	});

	const { mutate: restore, isPending: isRestoring } = useMutation({
		mutationKey: ["subscription", "restore"],
		mutationFn: async (subscriptionId: string) => {
			const result = await subscription.restore({ subscriptionId });
			return transformAuthResponse(result);
		},
		meta: {
			success: { message: m.subscription_restored() },
			error: { message: m.subscription_restoreFailed() },
		},
	});

	const { mutate: openBillingPortal, isPending: isOpeningPortal } = useMutation({
		mutationKey: ["subscription", "billing-portal"],
		mutationFn: async () => {
			const result = await subscription.billingPortal({
				returnUrl: "/settings/subscription",
			});
			if (result.data?.url) {
				window.location.href = result.data.url;
			}
			return result;
		},
		meta: {
			error: { message: m.billing_openPortalFailed() },
		},
	});

	const statusLabels = getStatusLabels();

	if (isPending) {
		return (
			<div className="space-y-4">
				<h4 className="text-base font-medium">{m.subscription_current()}</h4>
				<Skeleton className="h-24 w-full rounded-lg" />
			</div>
		);
	}

	const activeSubscription = subscriptions?.find(
		(s) => s.status === "active" || s.status === "trialing",
	);

	// better-auth may set cancelAt without updating cancelAtPeriodEnd
	const isPendingCancel = activeSubscription?.cancelAtPeriodEnd || !!activeSubscription?.cancelAt;

	const freePlan = getPlanByName("free")!;

	if (!activeSubscription) {
		return (
			<div className="space-y-4">
				<h4 className="text-base font-medium">{m.subscription_current()}</h4>
				<Card>
					<CardContent className="flex items-center gap-3 py-4">
						<CreditCardIcon className="size-5 text-muted-foreground" />
						<div>
							<p className="text-sm font-medium">
								{freePlan.label} {m.subscription_planSuffix()}
							</p>
							<p className="text-xs text-muted-foreground">{freePlan.description}</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const currentPlan = getPlanByName(activeSubscription.plan);
	const locale = getLocale();

	return (
		<div className="space-y-4">
			<h4 className="text-base font-medium">{m.subscription_current()}</h4>
			<Card>
				<CardContent className="flex items-center justify-between py-4">
					<div className="flex items-center gap-3">
						<CreditCardIcon className="size-5 text-muted-foreground" />
						<div>
							<div className="flex items-center gap-2">
								<p className="text-sm font-medium">
									{currentPlan?.label ?? activeSubscription.plan}{" "}
									{m.subscription_planSuffix()}
								</p>
								<Badge
									variant={
										statusVariants[activeSubscription.status ?? "incomplete"] ??
										"outline"
									}
								>
									{statusLabels[activeSubscription.status ?? "incomplete"] ??
										activeSubscription.status}
								</Badge>
							</div>
							{activeSubscription.periodEnd && (
								<p className="text-xs text-muted-foreground">
									{isPendingCancel
										? `${m.subscription_willExpirePrefix()} `
										: `${m.subscription_renewDate()} `}
									{new Date(activeSubscription.periodEnd).toLocaleDateString(
										locale,
									)}
									{isPendingCancel ? ` ${m.subscription_willExpireSuffix()}` : ""}
								</p>
							)}
						</div>
					</div>
					<div className="flex items-center gap-2">
						{isPendingCancel ? (
							<Button
								variant="outline"
								size="sm"
								disabled={isRestoring}
								onClick={() => restore(activeSubscription.stripeSubscriptionId!)}
							>
								{m.subscription_restore()}
							</Button>
						) : (
							<Button
								variant="outline"
								size="sm"
								className="text-destructive hover:text-destructive"
								disabled={isCanceling}
								onClick={() => cancel(activeSubscription.stripeSubscriptionId!)}
							>
								{m.subscription_cancel()}
							</Button>
						)}
						<Button
							variant="outline"
							size="sm"
							disabled={isOpeningPortal}
							onClick={() => openBillingPortal()}
						>
							<ExternalLinkIcon className="size-4 mr-1.5" />
							{m.subscription_manageBilling()}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function PlanSelector() {
	const [yearly, setYearly] = useState(false);
	const { data: subscriptions } = useSubscriptions();

	const { mutate: upgrade, isPending: isUpgrading } = useSubscriptionUpgrade({
		successUrl: "/settings/subscription",
		cancelUrl: "/settings/subscription",
	});

	const activeSubscription = subscriptions?.find(
		(s) => s.status === "active" || s.status === "trialing",
	);
	const currentPlan = activeSubscription?.plan ?? "free";

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h4 className="text-base font-medium">{m.subscription_selectPlan()}</h4>

				{/* Monthly / Yearly toggle */}
				<div className="inline-flex items-center rounded-full bg-muted p-1">
					<button
						type="button"
						className={cn(
							"rounded-full px-4 py-1 text-xs font-medium transition-all duration-200",
							!yearly
								? "bg-background text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
						onClick={() => setYearly(false)}
					>
						{m.pricing_monthly()}
					</button>
					<button
						type="button"
						className={cn(
							"rounded-full px-4 py-1 text-xs font-medium transition-all duration-200",
							yearly
								? "bg-background text-foreground shadow-sm"
								: "text-muted-foreground hover:text-foreground",
						)}
						onClick={() => setYearly(true)}
					>
						{m.pricing_yearly()}
					</button>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				{getPlans().map((plan) => {
					const isCurrent = currentPlan === plan.id;
					const displayPrice = yearly ? (plan.yearlyPrice ?? plan.price) : plan.price;
					const period = yearly ? m.plan_perYear() : m.plan_perMonth();

					return (
						<Card
							key={plan.id}
							className={cn(
								"flex flex-col",
								plan.highlighted && !isCurrent && "border-primary",
							)}
						>
							<CardHeader className="pb-3">
								<div className="flex items-center justify-between">
									<CardTitle className="text-base">{plan.label}</CardTitle>
									{isCurrent && (
										<Badge variant="secondary">
											{m.subscription_currentPlan()}
										</Badge>
									)}
									{plan.highlighted && !isCurrent && (
										<Badge>{m.subscription_recommended()}</Badge>
									)}
								</div>
								<p className="text-xs text-muted-foreground">{plan.description}</p>
							</CardHeader>
							<CardContent className="flex flex-1 flex-col space-y-4">
								<div>
									<span className="text-2xl font-bold">{displayPrice}</span>
									<span className="text-sm text-muted-foreground">{period}</span>
								</div>
								<ul className="space-y-2">
									{plan.features.map((feature) => (
										<li
											key={feature}
											className="flex items-center gap-2 text-sm"
										>
											<CheckIcon className="size-4 text-primary" />
											{feature}
										</li>
									))}
								</ul>
								<div className="mt-auto pt-2">
									{isCurrent ? (
										<Button
											variant="outline"
											size="sm"
											className="w-full"
											disabled
										>
											{m.subscription_currentPlan()}
										</Button>
									) : plan.id === "free" ? null : (
										<Button
											size="sm"
											className="w-full"
											disabled={isUpgrading}
											onClick={() =>
												upgrade({ plan: plan.id, annual: yearly })
											}
										>
											<SparklesIcon className="size-4 mr-1.5" />
											{m.subscription_upgradeTo()} {plan.label}
										</Button>
									)}
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		</div>
	);
}

function CreditPackSelector() {
	const { mutate: buyPack, isPending } = useMutation({
		mutationKey: ["credit-pack", "checkout"],
		mutationFn: async (packId: string) => {
			const result = await $createCreditPackCheckout({ data: { packId } });
			if (result.url) {
				window.location.href = result.url;
			}
			return result;
		},
		meta: {
			error: { message: m.creditPack_purchaseFailed() },
		},
	});

	return (
		<div className="space-y-4">
			<div>
				<h4 className="text-base font-medium">{m.creditPack_title()}</h4>
				<p className="text-sm text-muted-foreground">{m.creditPack_description()}</p>
			</div>

			<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
				{CREDIT_PACKS.map((pack) => {
					const priceDisplay = `$${(pack.priceUsd / 100).toFixed(2)}`;

					return (
						<Card key={pack.id} className="flex flex-col">
							<CardHeader className="pb-3">
								<div className="flex items-center justify-between">
									<CardTitle className="text-sm capitalize">{pack.id}</CardTitle>
									{pack.id === "mega" && (
										<Badge variant="secondary">
											{m.creditPack_bestValue()}
										</Badge>
									)}
									{pack.id === "standard" && (
										<Badge variant="outline">{m.creditPack_popular()}</Badge>
									)}
								</div>
							</CardHeader>
							<CardContent className="flex flex-1 flex-col space-y-3">
								<div>
									<span className="text-2xl font-bold">{priceDisplay}</span>
								</div>
								<p className="text-sm text-muted-foreground">
									{m.creditPack_credits({
										credits: pack.credits.toLocaleString(),
									})}
								</p>
								<div className="mt-auto pt-2">
									<Button
										variant="outline"
										size="sm"
										className="w-full"
										disabled={isPending}
										onClick={() => buyPack(pack.id)}
									>
										{m.creditPack_buy()}
									</Button>
								</div>
							</CardContent>
						</Card>
					);
				})}
			</div>
		</div>
	);
}
